import {
  Box,
  Grid,
  Flex,
  Avatar,
  Text,
  useColorModeValue,
  Icon,
  Badge,
  VStack,
  HStack,
  SimpleGrid,
  useToast,
  Center,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import {
  FiPhone,
  FiMapPin,
  FiFlag,
  FiGlobe,
  FiUser,
  FiBriefcase,
  FiHome,
  FiFolder,
  FiCalendar,
  FiAward,
  FiLayers,
  FiPackage,
  FiTool,
  FiTarget,
  FiClipboard,
} from 'react-icons/fi';
import { FaUser } from 'react-icons/fa';
import avatar from 'assets/img/avatars/USER_LOGO.webp';
import baseUrl from 'Base_Url/baseUrl';
import axiosInstance from 'axiosInstance';

export default function Overview() {
  const toast = useToast();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cardBg = useColorModeValue('white', 'gray.700');
  const cardTextColor = useColorModeValue('gray.700', 'white');
  const bannerBg = useColorModeValue(
    'linear-gradient(135deg, #90cdf4, #63b3ed, #3182ce)',
    'linear-gradient(135deg, #2c5282, #2a4365, #1a365d)',
  );

  const loaderBg = useColorModeValue('gray.50', 'gray.900');
  const loaderPattern = useColorModeValue(
    'radial-gradient(circle at 25px 25px, rgba(120, 119, 198, 0.3) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(120, 119, 198, 0.3) 2%, transparent 0%)',
    'radial-gradient(circle at 25px 25px, rgba(66, 153, 225, 0.15) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(66, 153, 225, 0.15) 2%, transparent 0%)',
  );
  const loaderGradient = useColorModeValue(
    'linear(to-r, blue.400, purple.500)',
    'linear(to-r, blue.500, purple.600)',
  );

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axiosInstance.get(`${baseUrl}/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status >= 200 && response.status < 300) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const profileData = await fetchUserProfile();
        setUser(profileData.data);
        setError(null);
      } catch (error) {
        setError(error.message);
        toast({
          title: 'Error',
          description:
            error.response?.data?.message ||
            error.response?.data?.data ||
            error.message ||
            'Failed to fetch profile',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  if (loading) {
    return (
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        bg={loaderBg}
        position="relative"
        overflow="hidden"
        borderRadius="20px"
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          backgroundImage={loaderPattern}
          backgroundSize="100px 100px"
        />

        <Flex direction="column" align="center" gap={6}>
          <Box
            width="100px"
            height="100px"
            borderRadius="full"
            bgGradient={loaderGradient}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FaUser} boxSize={12} color="white" />
          </Box>
        </Flex>
      </Flex>
    );
  }

  if (error) {
    return (
      <Center h="100vh">
        <Box textAlign="center">
          <Text fontSize="xl" color="red.500" mb={2}>
            Error Loading Profile
          </Text>
          <Text color="gray.600">{error}</Text>
        </Box>
      </Center>
    );
  }

  if (!user) {
    return (
      <Center h="100vh">
        <Box textAlign="center">
          <Text fontSize="xl" color="gray.500">
            No user data available
          </Text>
        </Box>
      </Center>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusColor = (active) => {
    return active ? 'green' : 'red';
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      admin: 'ADMIN',
      schemeAdmin: 'OFFICE IN CHARGE ( OIC )',
      talukaUser: 'TEHSILDAR',
      subDivisionalOfficer: 'SUB DIVISIONAL OFFICER ( SDO )',
      projectOfficer: 'PROJECT OFFICER ( PO )',
      departmentOfficer: 'DEPARTMENT OFFICER',
      departmentUser: 'DEPARTMENT USER',
    };
    return roleMap[role] || role?.toUpperCase() || 'NO ROLE';
  };

  const getBasicInfoFields = () => {
    const fields = [
      {
        icon: FiUser,
        label: 'User ID',
        value: user?.userId,
      },
      {
        icon: FiPhone,
        label: 'Phone Number',
        value: user?.phoneNo,
      },
      {
        icon: FiCalendar,
        label: 'Last Login',
        value: user?.lastLogin ? formatDate(user.lastLogin) : 'Never',
      },
    ];

    if (user?.role !== 'admin') {
      fields.push(
        {
          icon: FiAward,
          label: 'Designation',
          value: user?.designation || 'Not specified',
        },
        {
          icon: FiHome,
          label: 'Office',
          value: user?.nameOfOffice || 'Not specified',
        },
      );
    }

    return fields;
  };

  const basicInfoFields = getBasicInfoFields();

  const getDepartmentFields = () => {
    if (user?.role === 'admin') {
      return [];
    }

    const fields = [];

    if (
      user?.department &&
      Array.isArray(user.department) &&
      user.department.length > 0 &&
      user.role !== 'talukaUser'
    ) {
      const departmentNames = user.department
        .map((dept) => dept?.name)
        .filter(Boolean)
        .join(', ');

      fields.push({
        icon: FiMapPin,
        label: user.department.length > 1 ? 'Departments' : 'Department',
        value: departmentNames || 'Not assigned',
      });
    }

    if (user?.subDivision) {
      fields.push({
        icon: FiGlobe,
        label: 'Sub Division',
        value: user?.subDivision?.name || 'Not assigned',
      });
    }

    return fields;
  };

  const departmentFields = getDepartmentFields();

  const hasSchemes = () => {
    if (user?.role === 'admin') return false;
    return user?.schemes && user.schemes.length > 0;
  };

  const hasTahsils = () => {
    return (
      user?.role === 'talukaUser' && user?.tahsils && user.tahsils.length > 0
    );
  };

  const hasFileTypes = () => {
    return (
      (user?.role === 'projectOfficer' ||
        user?.role === 'subDivisionalOfficer') &&
      user?.fileTypes &&
      user.fileTypes.length > 0
    );
  };

  const getSchemeIcon = () => {
    const iconMap = {
      schemeAdmin: FiPackage,
      talukaUser: FiLayers,
      projectOfficer: FiTarget,
      departmentOfficer: FiTool,
      departmentUser: FiPackage,
      subDivisionalOfficer: FiClipboard,
    };
    return iconMap[user?.role] || FiFlag;
  };

  const getSchemeLabel = () => {
    const labelMap = {
      projectOfficer: 'Assigned Project Schemes',
      departmentOfficer: 'Assigned Department Schemes',
      subDivisionalOfficer: 'Assigned Schemes',
      schemeAdmin: 'Administered Schemes',
      talukaUser: 'Assigned Schemes',
      departmentUser: 'Assigned Schemes',
    };
    return labelMap[user?.role] || 'Assigned Schemes';
  };

  const getRoleSpecificInfo = () => {
    const fields = [];

    switch (user?.role) {
      case 'subDivisionalOfficer':
        fields.push({
          icon: FiClipboard,
          label: 'Jurisdiction',
          value: 'Sub-Division Level Operations',
        });
        break;

      case 'projectOfficer':
        fields.push({
          icon: FiTarget,
          label: 'Project Focus',
          value: 'Project Implementation & Monitoring',
        });
        break;

      case 'departmentOfficer':
        fields.push({
          icon: FiTool,
          label: 'Department Role',
          value: 'Departmental Supervision',
        });
        break;

      case 'schemeAdmin':
        fields.push({
          icon: FiPackage,
          label: 'Administration Level',
          value: 'Scheme Administration',
        });
        break;

      case 'talukaUser':
        fields.push({
          icon: FiLayers,
          label: 'Geographic Scope',
          value: 'Taluka Level Operations',
        });
        break;
    }

    return fields;
  };

  const roleSpecificFields = getRoleSpecificInfo();

  return (
    <Box
      pt={{ base: '140px', md: '90px', xl: '90px', sm: '100px' }}
      px={{ base: '4', md: '8' }}
    >
      <Flex
        direction="column"
        align="center"
        bg={bannerBg}
        borderRadius="lg"
        p={{ base: 6, md: 8 }}
        mb={6}
        boxShadow="lg"
        position="relative"
      >
        <Avatar
          size="2xl"
          src={avatar}
          alt="User Avatar"
          mb={4}
          boxShadow="lg"
          borderWidth={3}
          borderColor={cardBg}
        />
        <VStack spacing={2}>
          <Text
            fontSize="2xl"
            fontWeight="bold"
            color={cardTextColor}
            textAlign="center"
          >
            {user?.name?.toUpperCase() || 'No Name Provided'}
          </Text>
          <HStack spacing={3}>
            <Badge
              colorScheme={getStatusColor(user?.active)}
              fontSize="md"
              px={3}
              py={1}
              borderRadius="full"
            >
              {user?.active ? 'ACTIVE' : 'INACTIVE'}
            </Badge>
            <Badge
              colorScheme="blue"
              fontSize="md"
              px={3}
              py={1}
              borderRadius="full"
            >
              {getRoleDisplay(user?.role)}
            </Badge>
          </HStack>
        </VStack>
      </Flex>

      <Box mb={8}>
        <Text fontSize="xl" fontWeight="bold" mb={4} color={cardTextColor}>
          Basic Information
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {basicInfoFields.map((field, idx) => (
            <InfoCard
              key={idx}
              icon={field.icon}
              label={field.label}
              value={field.value}
              cardBg={cardBg}
              cardTextColor={cardTextColor}
            />
          ))}
        </SimpleGrid>
      </Box>

      {roleSpecificFields.length > 0 && (
        <Box mb={8}>
          <Text fontSize="xl" fontWeight="bold" mb={4} color={cardTextColor}>
            Role Information
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {roleSpecificFields.map((field, idx) => (
              <InfoCard
                key={idx}
                icon={field.icon}
                label={field.label}
                value={field.value}
                cardBg={cardBg}
                cardTextColor={cardTextColor}
              />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {departmentFields.length > 0 && (
        <Box mb={8}>
          <Text fontSize="xl" fontWeight="bold" mb={4} color={cardTextColor}>
            Department & Location
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {departmentFields.map((field, idx) => (
              <InfoCard
                key={idx}
                icon={field.icon}
                label={field.label}
                value={field.value}
                cardBg={cardBg}
                cardTextColor={cardTextColor}
              />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {hasSchemes() && (
        <Box mb={8}>
          <Text fontSize="xl" fontWeight="bold" mb={4} color={cardTextColor}>
            {getSchemeLabel()} ({user.schemes.length})
          </Text>
          <Grid
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            }}
            gap={4}
          >
            {user.schemes.map((scheme, idx) => (
              <Box
                key={scheme?._id || idx}
                bg={cardBg}
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderLeft="4px solid"
                borderLeftColor="blue.400"
              >
                <Flex align="center" gap={2} mb={2}>
                  <Icon as={getSchemeIcon()} boxSize={4} color="blue.400" />
                  <Text fontWeight="bold" color={cardTextColor}>
                    {scheme?.code || `Scheme ${idx + 1}`}
                  </Text>
                </Flex>
                <Text fontSize="sm" color="gray.500">
                  {scheme?.name || `Scheme ${idx + 1}`}
                </Text>
                {scheme?.department && (
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Department: {scheme.department.name}
                  </Text>
                )}
              </Box>
            ))}
          </Grid>
        </Box>
      )}

      {user?.role !== 'admin' &&
        (!user?.schemes || user.schemes.length === 0) && (
          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            textAlign="center"
            mb={6}
            boxShadow="sm"
            border="1px solid"
            borderColor="orange.200"
          >
            <Icon as={getSchemeIcon()} boxSize={8} color="orange.400" mb={2} />
            <Text fontWeight="medium" color="orange.600" mb={1}>
              No Schemes Assigned
            </Text>
            <Text fontSize="sm" color="gray.500">
              According to system rules, {getRoleDisplay(user.role)} should have
              at least one scheme assigned. Please contact administrator.
            </Text>
          </Box>
        )}

      {hasTahsils() && (
        <Box mb={8}>
          <Text fontSize="xl" fontWeight="bold" mb={4} color={cardTextColor}>
            Assigned Tahsils ({user.tahsils.length})
          </Text>
          <Grid
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            }}
            gap={4}
          >
            {user.tahsils.map((tahsil, idx) => (
              <Box
                key={tahsil?._id || idx}
                bg={cardBg}
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderLeft="4px solid"
                borderLeftColor="green.400"
              >
                <Flex align="center" gap={2} mb={2}>
                  <Icon as={FiMapPin} boxSize={4} color="green.400" />
                  <Text fontWeight="bold" color={cardTextColor}>
                    Tahsil
                  </Text>
                </Flex>
                <Text fontSize="sm" color="gray.500">
                  {tahsil?.name || `Tahsil ${idx + 1}`}
                </Text>
                {tahsil?.code && (
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Code: {tahsil.code}
                  </Text>
                )}
              </Box>
            ))}
          </Grid>
        </Box>
      )}

      {user?.role === 'talukaUser' &&
        (!user?.tahsils || user.tahsils.length === 0) && (
          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            textAlign="center"
            mb={6}
            boxShadow="sm"
            border="1px solid"
            borderColor="orange.200"
          >
            <Icon as={FiMapPin} boxSize={8} color="orange.400" mb={2} />
            <Text fontWeight="medium" color="orange.600" mb={1}>
              No Tahsils Assigned
            </Text>
            <Text fontSize="sm" color="gray.500">
              Taluka User should have at least one tahsil assigned according to
              system rules. Please contact administrator.
            </Text>
          </Box>
        )}

      {hasFileTypes() && (
        <Box mb={8}>
          <Text fontSize="xl" fontWeight="bold" mb={4} color={cardTextColor}>
            Assigned File Types ({user.fileTypes.length})
          </Text>
          <Grid
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            }}
            gap={4}
          >
            {user.fileTypes.map((fileType, idx) => (
              <Box
                key={fileType?._id || idx}
                bg={cardBg}
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderLeft="4px solid"
                borderLeftColor="purple.400"
              >
                <Flex align="center" gap={2} mb={2}>
                  <Icon as={FiFolder} boxSize={4} color="purple.400" />
                  <Text fontWeight="bold" color={cardTextColor}>
                    File Type
                  </Text>
                </Flex>
                <Text fontSize="sm" color="gray.500">
                  {fileType?.name || `File Type ${idx + 1}`}
                </Text>
                {fileType?.code && (
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Code: {fileType.code}
                  </Text>
                )}
                {fileType?.scheme && (
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Scheme: {fileType.scheme.name}
                  </Text>
                )}
              </Box>
            ))}
          </Grid>
        </Box>
      )}

      {(user?.role === 'projectOfficer' ||
        user?.role === 'subDivisionalOfficer') &&
        (!user?.fileTypes || user.fileTypes.length === 0) && (
          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            textAlign="center"
            mb={6}
            boxShadow="sm"
            border="1px solid"
            borderColor="orange.200"
          >
            <Icon as={FiFolder} boxSize={8} color="orange.400" mb={2} />
            <Text fontWeight="medium" color="orange.600" mb={1}>
              No File Types Assigned
            </Text>
            <Text fontSize="sm" color="gray.500">
              {getRoleDisplay(user.role)} should have at least one file type
              assigned according to system rules. Please contact administrator.
            </Text>
          </Box>
        )}
    </Box>
  );
}

const InfoCard = ({ icon, label, value, cardBg, cardTextColor }) => {
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box
      bg={cardBg}
      p={4}
      borderRadius="lg"
      boxShadow="sm"
      transition="transform 0.3s ease"
      _hover={{ transform: 'scale(1.03)' }}
      border="1px solid"
      borderColor={borderColor}
    >
      <Text fontWeight="bold" color={cardTextColor} mb={2} fontSize="sm">
        {label}
      </Text>
      <Flex align="center" gap={3}>
        <Icon as={icon} boxSize={4} color="blue.400" flexShrink={0} />
        <Text
          fontSize="sm"
          color={secondaryTextColor}
          fontWeight="medium"
          noOfLines={2}
        >
          {value || 'Not specified'}
        </Text>
      </Flex>
    </Box>
  );
};
